import { useEffect, useState } from 'react'
import {
  createFileRoute,
  Outlet,
  Link,
  redirect,
  useLocation,
} from '@tanstack/react-router'
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  Gauge,
  Inbox,
  KeyRound,
  Loader2,
  LogOut,
  Search,
  ShieldAlert,
  ShieldCheck,
  Terminal,
  UserCog,
  UserPlus,
  WifiOff,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiJson } from '@/lib/api/client'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import { useAuth } from '../lib/auth/useAuth'
import { CommandPalette } from '@/components/CommandPalette'
import { PageMain } from '@/components/page-main'
import { RealtimeProvider } from '@/lib/realtime/RealtimeHub'
import { AuditRealtimeSubscriptions } from '@/components/audit/realtime-subscriptions'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
} from '@/components/ui/sidebar'

export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    // Platform-admin routes do not require an active org (they are global by
    // definition), so platform admins can always reach them.
    const isPlatformAdminPath = location.pathname.startsWith('/admin')

    if (
      context.auth.isAuthenticated &&
      !context.auth.currentOrgSlug &&
      !isPlatformAdminPath
    ) {
      // Anyone without an active org goes through onboarding. For regular
      // users this means accepting a pending invite; for platform admins it
      // means creating or picking an org to act in.
      throw redirect({ to: '/onboarding' })
    }
  },
  component: AppLayout,
})

type Path =
  | '/dashboard'
  | '/packages'
  | '/org'
  | '/org/repositories'
  | '/org/requests'
  | '/org/policies'
  | '/org/security'
  | '/org/invites'
  | '/admin/organizations'
  | '/admin/audit'
  | '/admin/audit/mirrors'
  | '/admin/settings'
  | '/org/audit'
  | '/settings'
  | '/settings/security'
  | '/settings/tokens'

type NavLeaf = {
  kind?: 'leaf'
  to: Path
  label: string
  icon: React.ComponentType<{ className?: string }>
  badgeKey?: 'pendingRequests' | 'securityFlagged'
}

/**
 * Collapsible foldout containing one level of leaf items. The group label
 * doesn't navigate; clicking it toggles expansion. When any child is the
 * active route, the group auto-expands on mount.
 */
type NavGroup = {
  kind: 'group'
  /** Stable id used to persist expansion state in localStorage. */
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  children: NavLeaf[]
}

type NavItem = NavLeaf | NavGroup

function isGroup(item: NavItem): item is NavGroup {
  return (item as NavGroup).kind === 'group'
}

/** Walks the item tree and returns the flat list of leaf items. */
function leaves(items: NavItem[]): NavLeaf[] {
  return items.flatMap((it) => (isGroup(it) ? it.children : [it]))
}

const WORKSPACE: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: Gauge },
  { to: '/packages', label: 'Packages', icon: Boxes },
]

const OPERATE: NavItem[] = [
  { to: '/org', label: 'Overview', icon: Gauge },
  { to: '/org/repositories', label: 'Repositories', icon: Database },
  {
    to: '/org/requests',
    label: 'Requests',
    icon: Inbox,
    badgeKey: 'pendingRequests',
  },
  { to: '/org/policies', label: 'Policies', icon: ShieldCheck },
  {
    to: '/org/security',
    label: 'Security',
    icon: ShieldAlert,
    badgeKey: 'securityFlagged',
  },
  { to: '/org/audit', label: 'Audit log', icon: FileText },
  { to: '/org/invites', label: 'Invitations', icon: UserPlus },
]

const ACCOUNT: NavItem[] = [
  { to: '/settings', label: 'Profile', icon: UserCog },
  { to: '/settings/security', label: 'Security', icon: KeyRound },
  { to: '/settings/tokens', label: 'Tokens', icon: Terminal },
]

const PLATFORM_ADMIN: NavItem[] = [
  { to: '/admin/organizations', label: 'Organizations', icon: Boxes },
  {
    kind: 'group',
    id: 'admin-audit',
    label: 'Audit',
    icon: FileText,
    children: [
      { to: '/admin/audit', label: 'Logs', icon: FileText },
      { to: '/admin/audit/mirrors', label: 'Mirrors', icon: Database },
    ],
  },
  { to: '/admin/settings', label: 'System settings', icon: ShieldCheck },
]

function AppLayout() {
  const {
    user,
    logout,
    isLoading,
    isAuthenticated,
    hasOrgRole,
    isPlatformAdmin,
    currentOrgSlug,
    orgs,
    switchOrg,
    error,
  } = useAuth()
  const location = useLocation()
  const currentPath = location.pathname
  const [commandOpen, setCommandOpen] = useState(false)

  // Global ⌘K / Ctrl+K to open the command palette.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setCommandOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Find the nav leaf whose `to` is the longest prefix of the current path.
  // This makes /packages/pypi light up Packages, /org/requests light up
  // Requests (not Overview), etc. Groups are flattened to their leaves so a
  // child route activates the right entry inside the foldout.
  const allLeaves = leaves([...WORKSPACE, ...OPERATE, ...ACCOUNT, ...PLATFORM_ADMIN])
  const activeKey = allLeaves.reduce<string>((best, item) => {
    const isPrefix =
      currentPath === item.to || currentPath.startsWith(item.to + '/')
    if (isPrefix && item.to.length > best.length) return item.to
    return best
  }, '')

  const showAdmin = hasOrgRole('admin')
  const showPlatformAdmin = isPlatformAdmin

  // Hooks must run on every render; queryFns are gated by `enabled`.
  const pendingRequests = useOrgQuery<Array<{ status: string }>, number>(
    ['admin-requests-open'],
    '/admin/requests?limit=200',
    {
      enabled: isAuthenticated && showAdmin,
      refetchInterval: 30_000,
      staleTime: 25_000,
      // "Open" = anything that still needs an admin decision.
      select: (list) =>
        Array.isArray(list)
          ? list.filter((r) =>
              ['pending', 'verified', 'unverified'].includes(r.status),
            ).length
          : 0,
    },
  )

  const securityFlagged = useOrgQuery<Array<{ state: string }>, number>(
    ['admin-lifecycle-events'],
    '/admin/lifecycle/events',
    {
      // Only Quarantined drives the badge: that's the automatic, undecided state.
      // Blacklisted/Deprecated/Deleted are deliberate admin decisions and don't
      // need ongoing nagging once made.
      select: (list) => list.filter((e) => e.state === 'quarantined').length,
      enabled: isAuthenticated && showAdmin,
      refetchInterval: 30_000,
      staleTime: 25_000,
    },
  )

  // Platform admins are not tied to any single org. The org switcher lists
  // every org in the system so they can change context freely without ever
  // becoming a member.
  const platformAdminOrgs = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () =>
      apiJson<Array<{ id: string; name: string; display_name: string | null }>>(
        '/api/admin/organizations',
      ),
    enabled: isAuthenticated && isPlatformAdmin,
    staleTime: 60_000,
  })

  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated && !error) return <LoadingScreen label="Redirecting" />
  if (error) return <ErrorScreen />

  const initial = (user?.name ?? user?.email ?? 'U').charAt(0).toUpperCase()

  // Build the dropdown options: members see only their memberships (with role
  // suffix); platform admins see every org (no role - they transcend orgs).
  const switcherOptions: Array<{
    key: string
    slug: string
    label: string
  }> = isPlatformAdmin
    ? (platformAdminOrgs.data ?? []).map((o) => ({
        key: o.id,
        slug: o.name,
        label: o.display_name ?? o.name,
      }))
    : orgs.map((o) => ({
        key: o.organization_id,
        slug: o.slug,
        label: `${o.display_name ?? o.slug} (${o.role})`,
      }))

  const badgeCounts = {
    pendingRequests: pendingRequests.data ?? 0,
    securityFlagged: securityFlagged.data ?? 0,
  }

  return (
    <RealtimeProvider>
    <AuditRealtimeSubscriptions />
    <SidebarProvider>
      <Sidebar collapsible="icon" className="border-r border-sidebar-border">
        <SidebarHeader className="px-3 pt-4 pb-3">
          <Link to="/dashboard" className="flex items-center gap-2.5 px-2 group">
            <HawkMark />
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-semibold tracking-[-0.02em] text-foreground">
                hawk
              </span>
              <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70 mt-0.5 flex items-center gap-1.5">
                <span>v0.1</span>
                <span className="px-1 py-px rounded bg-primary/15 text-primary tracking-wider">
                  beta
                </span>
              </span>
            </div>
          </Link>

          <button
            type="button"
            onClick={() => setCommandOpen(true)}
            className="mt-4 w-full h-8 pl-2.5 pr-2 rounded-md bg-sidebar-accent border border-transparent hover:border-primary/30 hover:bg-secondary text-left flex items-center gap-2 transition-colors group/search"
            aria-label="Open command palette"
          >
            <Search
              className="size-3.5 text-muted-foreground/70 shrink-0"
              strokeWidth={1.5}
            />
            <span className="flex-1 text-xs text-muted-foreground/60 font-mono tracking-tight">
              Search…
            </span>
            <span className="text-[9px] font-mono text-muted-foreground/60 bg-background/60 rounded px-1.5 py-0.5 border border-sidebar-border">
              ⌘K
            </span>
          </button>

          {switcherOptions.length > 0 && (
            <div className="mt-3 px-1">
              <label className="block font-mono text-[9px] uppercase tracking-[0.26em] text-muted-foreground/60 mb-1.5">
                Organization
              </label>
              <select
                value={currentOrgSlug ?? ''}
                onChange={(e) => {
                  if (e.target.value) {
                    switchOrg(e.target.value).catch(() => {
                      /* surfaced by react-query refetch */
                    })
                  }
                }}
                className="w-full h-8 px-2 rounded-md bg-sidebar-accent border border-transparent hover:border-primary/30 text-xs font-mono tracking-tight focus:outline-none focus:border-primary/50"
                aria-label="Active organization"
              >
                {switcherOptions.map((o) => (
                  <option key={o.key} value={o.slug}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </SidebarHeader>

        <SidebarContent className="px-2 py-3">
          <NavSection
            label="Registry"
            items={WORKSPACE}
            activeKey={activeKey}
            badges={badgeCounts}
          />
          {showAdmin && (
            <NavSection
              label="Admin"
              items={OPERATE}
              activeKey={activeKey}
              badges={badgeCounts}
            />
          )}
          {showPlatformAdmin && (
            <NavSection
              label="Platform admin"
              items={PLATFORM_ADMIN}
              activeKey={activeKey}
              badges={badgeCounts}
            />
          )}
          <NavSection
            label="You"
            items={ACCOUNT}
            activeKey={activeKey}
            badges={badgeCounts}
          />
        </SidebarContent>

        <SidebarFooter className="p-2 border-t border-sidebar-border bg-background/40">
          <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-sidebar-accent/60 transition-colors">
            <Avatar className="size-7 ring-1 ring-primary/30">
              <AvatarFallback className="text-xs bg-secondary font-medium font-mono">
                {initial}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate leading-tight">
                {user?.name ?? 'User'}
              </div>
              <div className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5 font-mono">
                {user?.email ?? ''}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-foreground"
              onClick={logout}
              title="Sign out"
            >
              <LogOut className="size-3.5" />
              <span className="sr-only">Sign out</span>
            </Button>
          </div>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset id="main-content" className="bg-background">
        <Outlet />
      </SidebarInset>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </SidebarProvider>
    </RealtimeProvider>
  )
}

function NavSection({
  label,
  items,
  activeKey,
  badges,
}: {
  label: string
  items: NavItem[]
  activeKey: string
  badges: Record<string, number>
}) {
  return (
    <SidebarGroup className="px-0 py-1.5">
      <div className="px-3 mb-2 flex items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-muted-foreground/60 font-medium">
          {label}
        </span>
        <span className="h-px flex-1 bg-sidebar-border" />
      </div>
      <nav aria-label={label} className="flex flex-col gap-0.5">
        {items.map((item) =>
          isGroup(item) ? (
            <NavGroupRow
              key={item.id}
              group={item}
              activeKey={activeKey}
              badges={badges}
            />
          ) : (
            <NavLeafRow
              key={item.to}
              item={item}
              active={item.to === activeKey}
              badge={item.badgeKey ? badges[item.badgeKey] : undefined}
            />
          ),
        )}
      </nav>
    </SidebarGroup>
  )
}

function NavLeafRow({
  item,
  active,
  badge,
  indented,
}: {
  item: NavLeaf
  active: boolean
  badge?: number
  indented?: boolean
}) {
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={`relative flex items-center gap-2.5 mx-1 ${indented ? 'pl-9 pr-3' : 'px-3'} h-8 rounded-md text-[13px] transition-all group ${
        active
          ? 'bg-sidebar-accent text-foreground'
          : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50'
      }`}
    >
      {active && (
        <>
          <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-full bg-primary" />
          <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-full bg-primary blur-[2px] opacity-60" />
        </>
      )}
      <Icon
        className={`size-4 shrink-0 transition-colors ${
          active
            ? 'text-primary'
            : 'text-muted-foreground/70 group-hover:text-foreground'
        }`}
      />
      <span className="truncate flex-1">{item.label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded font-mono text-[10px] font-medium tabular-nums bg-primary/15 text-primary ring-1 ring-inset ring-primary/30">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  )
}

function NavGroupRow({
  group,
  activeKey,
  badges,
}: {
  group: NavGroup
  activeKey: string
  badges: Record<string, number>
}) {
  const hasActiveChild = group.children.some((c) => c.to === activeKey)
  const storageKey = `hawk.nav.group.${group.id}`
  const [expanded, setExpanded] = useState<boolean>(() => {
    // Auto-expand if a child is currently active; otherwise restore the
    // user's last choice from localStorage. Defaults to collapsed for a
    // fresh visitor so the sidebar stays compact.
    if (hasActiveChild) return true
    if (typeof window === 'undefined') return false
    const stored = window.localStorage.getItem(storageKey)
    return stored === '1'
  })

  // If navigation brings a child into the active state while collapsed,
  // open the foldout so the active row is visible.
  useEffect(() => {
    if (hasActiveChild && !expanded) setExpanded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveChild])

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, next ? '1' : '0')
      }
      return next
    })
  }

  const Icon = group.icon
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className={`relative w-full flex items-center gap-2.5 mx-1 px-3 h-8 rounded-md text-[13px] transition-all group ${
          hasActiveChild
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50'
        }`}
        style={{ width: 'calc(100% - 0.5rem)' }}
      >
        <Icon
          className={`size-4 shrink-0 transition-colors ${
            hasActiveChild
              ? 'text-primary/80'
              : 'text-muted-foreground/70 group-hover:text-foreground'
          }`}
        />
        <span className="truncate flex-1 text-left">{group.label}</span>
        {expanded ? (
          <ChevronDown
            className="size-3.5 text-muted-foreground/50 group-hover:text-foreground/80 transition-colors"
            strokeWidth={1.75}
          />
        ) : (
          <ChevronRight
            className="size-3.5 text-muted-foreground/50 group-hover:text-foreground/80 transition-colors"
            strokeWidth={1.75}
          />
        )}
      </button>
      {expanded && (
        <div className="flex flex-col gap-0.5 mt-0.5">
          {group.children.map((child) => (
            <NavLeafRow
              key={child.to}
              item={child}
              active={child.to === activeKey}
              badge={child.badgeKey ? badges[child.badgeKey] : undefined}
              indented
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * HawkMark, geometric mark inspired by Cloudflare's lockup gravity.
 * A nested-bracket diamond with primary glow.
 */
function HawkMark() {
  return (
    <span className="relative inline-flex items-center justify-center size-7 shrink-0">
      <span className="absolute inset-0 rounded-md bg-primary/10 ring-1 ring-primary/20" />
      <svg viewBox="0 0 24 24" className="size-4 relative" aria-hidden>
        <path
          d="M12 2 L20 7 L20 17 L12 22 L4 17 L4 7 Z"
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="1.5"
        />
        <path
          d="M12 7 L16 9.5 L16 14.5 L12 17 L8 14.5 L8 9.5 Z"
          fill="var(--color-primary)"
          fillOpacity="0.85"
        />
      </svg>
    </span>
  )
}

function LoadingScreen({ label = 'Loading' }: { label?: string }) {
  return (
    <PageMain className="flex min-h-screen items-center justify-center">
      <div role="status" aria-live="polite" className="flex flex-col items-center gap-3">
        <Loader2
          className="size-5 text-primary animate-spin"
          strokeWidth={1.5}
          aria-hidden
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
          {label}
        </span>
      </div>
    </PageMain>
  )
}

function ErrorScreen() {
  return (
    <PageMain className="flex min-h-screen items-center justify-center p-6">
      <div role="alert" className="max-w-sm text-center space-y-4">
        <div className="flex items-center justify-center gap-2 mb-2 text-destructive">
          <WifiOff className="size-4" strokeWidth={1.5} aria-hidden />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em]">
            Disconnected
          </span>
        </div>
        <h1 className="text-2xl font-medium tracking-tight">Unable to reach Hawk</h1>
        <p className="text-muted-foreground text-sm">
          Make sure the server is running and your network can reach it.
        </p>
        <Button onClick={() => window.location.reload()}>Retry connection</Button>
      </div>
    </PageMain>
  )
}
